import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { Move, Maximize2, Check, X, ZoomIn } from "lucide-react";

// Drag-to-frame banner cropper. The user pans (and optionally zooms) the image inside a
// fixed-ratio frame; on apply we render exactly the visible region to a canvas at the target
// resolution and hand back a JPEG data-URL. "Auto-fit" re-centers to a clean cover fit.
export default function BannerCropper({ src, onApply, onCancel, targetW = 1600, targetH = 400 }) {
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [grabbing, setGrabbing] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      if (frameRef.current) {
        const w = frameRef.current.clientWidth;
        setFrame({ w, h: (w * targetH) / targetW });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [targetW, targetH]);

  const baseScale = nat.w && frame.w ? Math.max(frame.w / nat.w, frame.h / nat.h) : 1;
  const scale = baseScale * zoom;
  const imgW = nat.w * scale;
  const imgH = nat.h * scale;

  const clamp = (o) => {
    const minX = frame.w - imgW; // <= 0
    const minY = frame.h - imgH;
    return { x: Math.min(0, Math.max(minX, o.x)), y: Math.min(0, Math.max(minY, o.y)) };
  };
  const centerOffset = () => ({ x: (frame.w - imgW) / 2, y: (frame.h - imgH) / 2 });

  // Center once the image + frame are known.
  useEffect(() => {
    if (nat.w && frame.w) setOffset(centerOffset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nat.w, nat.h, frame.w, frame.h]);

  // Keep the pan valid when zoom changes.
  useEffect(() => {
    if (nat.w && frame.w) setOffset((o) => clamp(o));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
    setGrabbing(true);
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    setOffset(clamp({ x: dragRef.current.ox + (e.clientX - dragRef.current.sx), y: dragRef.current.oy + (e.clientY - dragRef.current.sy) }));
  };
  const onUp = () => { dragRef.current = null; setGrabbing(false); };

  const autoFit = () => { setZoom(1); setTimeout(() => setOffset(centerOffset()), 0); };

  const apply = () => {
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    const srcX = -offset.x / scale;
    const srcY = -offset.y / scale;
    const srcW = frame.w / scale;
    const srcH = frame.h / scale;
    ctx.drawImage(imgRef.current, srcX, srcY, srcW, srcH, 0, 0, targetW, targetH);
    onApply(canvas.toDataURL("image/jpeg", 0.88));
  };

  return (
    <div data-testid="banner-cropper">
      <div
        ref={frameRef}
        className="relative w-full overflow-hidden rounded-2xl bg-[#100d1c] select-none border-2 border-[#cfe0c7]"
        style={{ height: frame.h || 110, cursor: grabbing ? "grabbing" : "grab", touchAction: "none" }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
          style={{ position: "absolute", left: 0, top: 0, width: imgW || "auto", height: imgH || "auto", maxWidth: "none", transform: `translate(${offset.x}px, ${offset.y}px)`, willChange: "transform", pointerEvents: "none" }}
        />
        <div className="absolute inset-x-0 bottom-0 flex justify-center pb-2 pointer-events-none">
          <span className="text-[11px] font-bold text-white bg-black/55 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1">
            <Move size={12} /> Drag into place — we'll crop the rest
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2.5">
        <ZoomIn size={15} className="text-[#6b6880] shrink-0" />
        <input
          data-testid="banner-zoom"
          type="range" min="1" max="3" step="0.01" value={zoom}
          onChange={(e) => setZoom(parseFloat(e.target.value))}
          className="flex-1 accent-[#4f7746]"
        />
        <button data-testid="banner-autofit" type="button" onClick={autoFit} className="inline-flex items-center gap-1 text-xs font-bold text-[#3f6238] bg-[#eef3ea] px-2.5 py-1.5 rounded-full hover:bg-[#e2ecdc] transition-colors">
          <Maximize2 size={13} /> Auto-fit
        </button>
      </div>

      <div className="flex items-center gap-2 mt-2.5">
        <button data-testid="banner-crop-cancel" type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#6b6880] bg-[#f1f0f5] px-3.5 py-2 rounded-full hover:bg-[#e8e6ef] transition-colors">
          <X size={15} /> Cancel
        </button>
        <button data-testid="banner-crop-apply" type="button" onClick={apply} className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-extrabold text-white py-2 rounded-full transition-transform hover:scale-[1.02]" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
          <Check size={16} /> Use this framing
        </button>
      </div>
    </div>
  );
}
